/**
 * Pezzi comuni delle viste a calendario ed elenco, allenamenti (V10) e
 * partite (V16): la riga dei filtri con le icone della vista a destra, la
 * tendina di un filtro e la card di un gruppo dell'elenco, paginata.
 */

import { type ReactNode, useId } from 'react';
import { CalendarDaysIcon, ListIcon, type LucideIcon } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { usePaginazione } from './Paginazione.js';
import { Nota } from './Nota.js';

/** Le voci di una tendina Radix non accettano il valore vuoto: "tutti" e esplicito. */
export const TUTTI = 'tutti';

/** I filtri vanno a capo tra loro; le icone della vista restano sempre a destra. */
export function Filtri({
  calendario,
  onVista,
  children,
}: {
  calendario: boolean;
  onVista: (vista: 'calendario' | 'elenco') => void;
  children: ReactNode;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3">{children}</div>
      <ToggleGroup
        type="single"
        variant="outline"
        value={calendario ? 'calendario' : 'elenco'}
        onValueChange={(v) => (v === 'calendario' || v === 'elenco') && onVista(v)}
        aria-label="Vista"
      >
        <VoceVista valore="calendario" etichetta="Calendario" Icona={CalendarDaysIcon} />
        <VoceVista valore="elenco" etichetta="Elenco" Icona={ListIcon} />
      </ToggleGroup>
    </div>
  );
}

function VoceVista({
  valore,
  etichetta,
  Icona,
}: {
  valore: string;
  etichetta: string;
  Icona: LucideIcon;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <ToggleGroupItem value={valore} aria-label={etichetta}>
          <Icona />
        </ToggleGroupItem>
      </TooltipTrigger>
      <TooltipContent>{etichetta}</TooltipContent>
    </Tooltip>
  );
}

/**
 * La tendina di un filtro, senza etichetta sopra: il nome del filtro sta
 * dentro, al posto di «Tutti», in grigio come un segnaposto; scelto un
 * valore, resta il valore. Un filtro senza la voce «Tutti», come un
 * ordinamento, mostra nome e valore insieme («Ordina per: Cognome»).
 */
export function Filtro({
  etichetta,
  valore,
  onCambia,
  opzioni,
  tutti = TUTTI,
  className,
}: {
  etichetta: string;
  valore: string;
  onCambia: (valore: string) => void;
  opzioni: { valore: string; etichetta: string }[];
  /** Il valore della voce che non filtra; di norma `TUTTI`. */
  tutti?: string;
  /** In una griglia la cella decide la larghezza: `max-w-none` toglie il tetto. */
  className?: string;
}) {
  const id = useId();
  const libero = valore === tutti;
  const conTutti = opzioni.some((o) => o.valore === tutti);
  const scelta = opzioni.find((o) => o.valore === valore)?.etichetta;
  return (
    <div className={cn('flex min-w-28 flex-1 @xl:max-w-64', className)}>
      <Label htmlFor={id} className="sr-only">
        {etichetta}
      </Label>
      <Select value={valore} onValueChange={onCambia}>
        <SelectTrigger id={id} className={cn('w-full', libero && 'text-muted-foreground')}>
          <SelectValue>
            {libero ? (
              etichetta
            ) : conTutti ? (
              <span className="block truncate first-letter:uppercase">{scelta}</span>
            ) : (
              <span className="truncate">
                <span className="text-muted-foreground">{etichetta}:</span> {scelta}
              </span>
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {opzioni.map((o) => (
            <SelectItem key={o.valore} value={o.valore} className="first-letter:uppercase">
              {o.etichetta}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/** I numeri della stagione accanto al titolo, con l'icona e la spiegazione al passaggio. */
export function Riepilogo({ children }: { children: ReactNode }) {
  return <dl className="flex flex-wrap items-center gap-3 text-sm tabular-nums">{children}</dl>;
}

export function VoceRiepilogo({
  Icona,
  valore,
  titolo,
  colore,
}: {
  Icona: LucideIcon;
  valore: ReactNode;
  titolo: string;
  /** Il colore dell'icona, per i numeri che chiedono attenzione. */
  colore?: string;
}) {
  return (
    <div className="flex items-center gap-1" title={titolo}>
      <dt>
        <Icona aria-label={titolo} className={cn('size-4 text-muted-foreground', colore)} />
      </dt>
      <dd className="font-semibold">{valore}</dd>
    </div>
  );
}

/**
 * Righe per pagina delle card dei gruppi: stanno affiancate, e cinque righe le
 * tengono corte e vicine d'altezza.
 */
const PER_PAGINA_GRUPPO = 5;

/**
 * Un gruppo dell'elenco nella sua card, paginato; vuoto non c'e. La card
 * riempie l'altezza della riga della griglia, cosi due affiancate finiscono
 * allo stesso punto, e la barra delle pagine sta sul fondo.
 */
export function GruppoPaginato<T extends { id: string }>({
  titolo,
  Icona,
  colore,
  nota,
  elementi,
  chiave,
  perPagina = PER_PAGINA_GRUPPO,
  riga,
}: {
  titolo: string;
  Icona: LucideIcon;
  /** Il colore dell'icona, per il gruppo che chiede attenzione. */
  colore?: string;
  nota?: string;
  elementi: T[];
  /** La chiave della sua pagina nell'URL. */
  chiave: string;
  /** Righe per pagina; senza, cinque. */
  perPagina?: number;
  riga: (elemento: T) => ReactNode;
}) {
  const { righe, barra, inizio } = usePaginazione(elementi, chiave, perPagina);
  if (elementi.length === 0) return null;
  return (
    // Sotto la testata fissa del telefono, tornandoci cambiando pagina.
    <Card ref={inizio} className="h-full scroll-mt-(--altezza-testata) bg-muted">
      <CardHeader>
        <CardTitle className="flex items-center justify-center gap-2 font-semibold tracking-wide uppercase">
          <Icona aria-hidden className={cn('size-5', colore)} />
          <h2>{titolo}</h2>
          <Badge className="tabular-nums">{elementi.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-4 px-2 @xl:px-(--card-spacing)">
        {nota && <Nota className="px-1">{nota}</Nota>}
        <ul className="flex flex-col gap-2">
          {righe.map((e) => (
            <li key={e.id}>{riga(e)}</li>
          ))}
        </ul>
        {barra && <div className="mt-auto">{barra}</div>}
      </CardContent>
    </Card>
  );
}
